// A workflow lookup: the words the workflow engine shows people. Two families, edited differently
// (OLIO-2830).
//
// `option_list` — what a picker offers (denial reasons, denial types, competitors). The item set is
// open-ended, so entries can be added, renamed, reordered and archived.
//
// `display_text` — what a workflow's actions, values and categories are CALLED. One entry per thing the
// workflow declares, so the set is not the admin's to change: an added entry resolves to nothing
// and a removed one blanks a label in the live product. Rename only.
export type WorkflowLookupFamily = 'option_list' | 'display_text';

// How to render one extra field an item carries beyond its name.
//
// `lookup` is the dependent-pair case: the value must name an entry in another list, so it is a
// picker over that list rather than free text. `parentKey` says which list. Typing a parent that
// does not exist is the bug this replaces — it used to save without complaint and then never
// appear in the dropdown.
export type WorkflowLookupFieldType = 'text' | 'boolean' | 'enum' | 'lookup';

export interface WorkflowLookupFieldOption {
  value: string;
  label: string;
}

export interface WorkflowLookupField {
  // Spelled as the key sits on an item, so `item[field.name]` reads and writes it directly.
  name: string;
  label: string;
  type: WorkflowLookupFieldType;
  required: boolean;
  // `enum` only.
  options: WorkflowLookupFieldOption[];
  // `lookup` only: the key of the list whose entries are offered.
  parentKey: string | null;
}

export interface WorkflowLookupItem {
  // Client-only, assigned when the editor loads or adds an entry. React keys and the drag id both
  // read it, so a row keeps its identity while its short name is still being derived from what is
  // being typed. Never sent to the API.
  _key?: string;
  // Client-only: this entry has never been saved, so its short name still follows the name being
  // typed. Once saved the short name is permanent, because recorded referrals point at it.
  _isNew?: boolean;
  // The stored slug. Fixed once created — reports group by it, so renaming splits one thing's
  // history in two.
  apiName: string;
  displayName: string;
  description: string | null;
  // Removed rather than deleted, so referrals that already recorded it still render its words.
  archived: boolean;
  // Display text only: the live wording differs from what the code ships.
  customized: boolean;
  sourceDisplayName: string | null;
  sourceDescription: string | null;
  // Extra fields the key declares arrive alongside these, keyed by the field's own name.
  [field: string]: unknown;
}

export interface WorkflowLookupRegistryEntry {
  key: string;
  label: string;
  description: string | null;
  family: WorkflowLookupFamily;
  workflowApiName: string | null;
  workflowLabel: string | null;
  // False for display text: its entries come from the workflow.
  itemsEditable: boolean;
  fields: WorkflowLookupField[];
}

// One half of a declared pair: the other list, named. Derived on the API from
// WorkflowLookup::Dependencies, so the relationship is never written down twice.
export interface WorkflowLookupPair {
  key: string;
  label: string;
}

export interface WorkflowLookup extends WorkflowLookupRegistryEntry {
  id: string;
  clientId: string | null;
  updatedAt: string;
  // Null on a row nobody has edited yet — seeds and publish leave it empty.
  updatedByName: string | null;
  // False for a row whose key the code no longer asks for. It still lists, read-only.
  editable: boolean;
  global: boolean;
  clientName: string | null;
  activeItemCount: number;
  // The list this one's entries point into, and the lists that point into this one. Both empty for
  // display text, which names one workflow rather than another list.
  dependsOn: WorkflowLookupPair | null;
  usedBy: WorkflowLookupPair[];
  // How many names have been changed away from what the code declares. Always 0 for an option list,
  // which has no declared wording to differ from, so this can be read without checking the family.
  customizedItemCount: number;
  // Present on show / create / update, absent on the index.
  items?: WorkflowLookupItem[];
}

export const OPTION_LIST: WorkflowLookupFamily = 'option_list';
export const DISPLAY_TEXT: WorkflowLookupFamily = 'display_text';

export const FAMILY_LABELS: Record<WorkflowLookupFamily, string> = {
  option_list: 'Options list',
  display_text: 'Display text',
};
